import React, { useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "styles/react-datepicker-theme.css";
import AutocompleteTagFilter from "../AutocompleteTagFilter";
import Select from "../Select";

// shadcn
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogBody,
    DialogFooter,
    DialogTitle,
    DialogDescription,
    DialogTrigger,
} from "components/ui/dialog";

// icons
import { Filter, RotateCcw } from "lucide-react";

const simpleInputFields = [
    { label: "שם", field: "name" },
    { label: "פלאפון", field: "phone" },
    { label: "מוצר", field: "product" },
    { label: "הערות", field: "comments" },
];

const emptyFilters = {
    name: "",
    phone: "",
    status: "",
    product: "",
    comments: "",
    startDate: null,
    endDate: null,
    tags: [],
};

const isTruthy = (v) => (Array.isArray(v) ? v.length > 0 : !!v);

const OrdersFilterDialog = ({ onApply, initialFilters = {}, statuses = [] }) => {
    const [open, setOpen] = useState(false);
    const [filters, setFilters] = useState({ ...emptyFilters, ...initialFilters });

    const statusOptions = useMemo(
        () => [
            { value: "", label: "כל הסטטוסים", color: null },
            ...statuses.map((s) => ({ value: s._id, label: s.name, color: s.color })),
        ],
        [statuses]
    );

    // Important: keep draft synced when opening (prevents stale UI)
    useEffect(() => {
        if (!open) return;
        setFilters({ ...emptyFilters, ...initialFilters });
    }, [open, initialFilters]);

    const activeCount = useMemo(
        () =>
            ["name", "phone", "status", "product", "comments", "startDate", "endDate"].filter((k) =>
                isTruthy(filters[k])
            ).length + (filters.tags?.length ? 1 : 0),
        [filters]
    );

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleDateChange = (dates) => {
        const [start, end] = dates;
        setFilters((prev) => ({ ...prev, startDate: start, endDate: end }));
    };

    const handleTagChange = (selectedTags) => {
        setFilters((prev) => ({ ...prev, tags: selectedTags.map((t) => ({ ...t })) }));
    };

    const resetAll = () => {
        const clearedFilters = { ...emptyFilters };
        setFilters(clearedFilters);
        onApply?.(clearedFilters);
    };

    const apply = () => {
        onApply?.(filters);
        setOpen(false);
    };

    const isFilterActive = (field) => isTruthy(filters[field]);

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="secondary" className="gap-2">
                    <Filter className="h-4 w-4" />
                    <span className="hidden sm:inline">סינון</span>
                    {activeCount > 0 && (
                        <span className="rounded-md bg-background px-2 py-0.5 text-xs font-semibold">
                            {activeCount}
                        </span>
                    )}
                </Button>
            </DialogTrigger>

            <DialogContent dir="rtl">
                <DialogHeader>
                    <DialogTitle>סינון הזמנות</DialogTitle>
                    <DialogDescription>בחר מסננים להצגת ההזמנות הרלוונטיות.</DialogDescription>
                </DialogHeader>

                <DialogBody>
                    <div className="space-y-3">
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            {simpleInputFields.map((f) => (
                                <div key={f.field} className="space-y-1">
                                    <Label htmlFor={f.field}>{f.label}</Label>
                                    <Input
                                        id={f.field}
                                        name={f.field}
                                        value={filters[f.field]}
                                        onChange={handleInputChange}
                                        className={isFilterActive(f.field) ? "bg-accent/40" : ""}
                                        placeholder={f.label}
                                    />
                                </div>
                            ))}

                            <div className="space-y-1">
                                <Label>סטטוס</Label>
                                <Select
                                    options={statusOptions}
                                    initialValue={filters.status}
                                    onOptionChange={(v) => setFilters((prev) => ({ ...prev, status: v }))}
                                />
                            </div>

                            <div className="space-y-1">
                                <Label>טווח תאריכים</Label>
                                <DatePicker
                                    selected={filters.startDate}
                                    onChange={handleDateChange}
                                    startDate={filters.startDate}
                                    endDate={filters.endDate}
                                    selectsRange
                                    isClearable
                                    placeholderText="בחר טווח תאריכים"
                                    // Key: prevents popper/resize loops inside dialogs
                                    withPortal
                                    className={`w-full rounded-md border bg-background px-3 py-2 text-sm ring-offset-background ${isFilterActive("startDate") || isFilterActive("endDate") ? "bg-accent/40" : ""
                                        }`}
                                />
                            </div>

                            <div className="space-y-1 sm:col-span-2">
                                <Label>תגיות</Label>
                                <AutocompleteTagFilter
                                    selectedTags={filters.tags}
                                    setSelectedTags={handleTagChange}
                                />
                            </div>
                        </div>
                    </div>
                </DialogBody>

                <DialogFooter>
                    <Button type="button" variant="ghost" onClick={resetAll} className="gap-2">
                        <RotateCcw className="h-4 w-4" />
                        נקה הכל
                    </Button>
                    <Button type="button" onClick={apply} className="min-w-28">
                        החל
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default OrdersFilterDialog;
